import React, { useState } from "react";
import { IconButton } from "../../controls";
import "./UserFeedbackButtons.scss";

export const UserFeedbackStatus = {
  NONE: "NONE",
  LIKE: "LIKE",
  DISLIKE: "DISLIKE",
} as const;

export type UserFeedbackStatusValue = Exclude<
  (typeof UserFeedbackStatus)[keyof typeof UserFeedbackStatus],
  "NONE"
>;

export interface UserFeedbackButtonsProps {
  onFeedback?: (newFeedback: UserFeedbackStatusValue) => void;
  /** Stamped on both buttons as a `user_click` with `action` set per button. */
  analyticsProps?: Record<string, unknown>;
  /**
   * When true the buttons are non-interactive — used while an assistant
   * message is still streaming (its persisted id isn't available yet).
   */
  disabled?: boolean;
}

const UserFeedbackButtons: React.FC<UserFeedbackButtonsProps> = ({
  onFeedback,
  analyticsProps,
  disabled = false,
}) => {
  const [userFeedback, setUserFeedback] = useState<
    (typeof UserFeedbackStatus)[keyof typeof UserFeedbackStatus]
  >(UserFeedbackStatus.NONE);

  const getAnalyticsAttributes = (action: string) =>
    analyticsProps && {
      "data-analytics-event": "user_click",
      "data-analytics-props": JSON.stringify({ ...analyticsProps, action }),
    };

  const toggleUserFeedback = (newFeedback: UserFeedbackStatusValue) => {
    if (newFeedback === userFeedback) {
      setUserFeedback(UserFeedbackStatus.NONE);
    } else {
      setUserFeedback(newFeedback);
      onFeedback?.(newFeedback);
    }
  };

  return (
    <div className="user-feedback-buttons">
      <IconButton
        isIcon={
          userFeedback === UserFeedbackStatus.LIKE
            ? "seem-icon-like-fill"
            : "seem-icon-like"
        }
        size={"small"}
        wrapperClassName={"ai-like-button"}
        type={"button"}
        disabled={disabled}
        withTooltip={{ content: "Like" }}
        data-testid={`ai-like`}
        onClick={() => toggleUserFeedback(UserFeedbackStatus.LIKE)}
        {...getAnalyticsAttributes("feedback_like")}
      />
      <IconButton
        isIcon={
          userFeedback === UserFeedbackStatus.DISLIKE
            ? "seem-icon-dislike-fill"
            : "seem-icon-dislike"
        }
        size={"small"}
        className={"ai-dislike-button"}
        type={"button"}
        disabled={disabled}
        withTooltip={{ content: "Dislike" }}
        data-testid={`ai-dislike`}
        onClick={() => toggleUserFeedback(UserFeedbackStatus.DISLIKE)}
        {...getAnalyticsAttributes("feedback_dislike")}
      />
    </div>
  );
};

export default UserFeedbackButtons;
